import type { ApiKeyUsageDetail, DailyUsageData, ProviderUsageMetrics, UsageDetailsData, UsageProvider } from './openai-api';

type Provider = UsageProvider;

interface ModelAccumulator {
  model: string;
  lastUsed?: string;
  contextTokens: number;
  generatedTokens: number;
  cachedTokens: number;
  cost: number;
  numberOfRequests: number;
  webSearchCalls?: number | null;
  provider?: Provider;
}

interface ApiKeyAccumulator {
  id: string;
  entry: ApiKeyUsageDetail;
  provider?: Provider;
}

const cloneProviderMetrics = (metrics: ProviderUsageMetrics): ProviderUsageMetrics => ({
  provider: metrics.provider,
  requests: metrics.requests,
  cost: metrics.cost,
  contextTokens: metrics.contextTokens,
  generatedTokens: metrics.generatedTokens,
  cachedTokens: metrics.cachedTokens,
  isEstimatedRequests: metrics.isEstimatedRequests
});

const cloneBreakdowns = (
  input?: Partial<Record<Provider, ProviderUsageMetrics>>
): Partial<Record<Provider, ProviderUsageMetrics>> | undefined => {
  if (!input) return undefined;
  const result: Partial<Record<Provider, ProviderUsageMetrics>> = {};
  Object.entries(input).forEach(([key, metrics]) => {
    if (!metrics) return;
    result[key as Provider] = cloneProviderMetrics(metrics);
  });
  return result;
};

const ensureBreakdown = (
  target: DailyUsageData,
  providerKey: Provider
): ProviderUsageMetrics => {
  if (!target.breakdowns) {
    target.breakdowns = {};
  }
  if (!target.breakdowns[providerKey]) {
    target.breakdowns[providerKey] = {
      provider: providerKey,
      requests: 0,
      cost: 0,
      contextTokens: 0,
      generatedTokens: 0,
      cachedTokens: 0,
      isEstimatedRequests: providerKey === 'anthropic'
    };
  }
  return target.breakdowns[providerKey]!;
};

export interface UsageChartMergeSource {
  provider: Provider;
  dailyUsage: DailyUsageData[];
}

export interface MergeUsageForChartsOptions {
  join?: 'outer' | 'inner';
}

const getProvider = (value?: string): Provider | undefined => {
  if (value === 'openai' || value === 'anthropic') {
    return value;
  }
  return undefined;
};

export const mergeUsageDetailsData = (sources: UsageDetailsData[]): UsageDetailsData => {
  if (sources.length === 0) {
    throw new Error('mergeUsageDetailsData requires at least one source');
  }

  const modelMap = new Map<string, ModelAccumulator>();
  const apiKeyMap = new Map<string, ApiKeyAccumulator>();
  const dailyMap = new Map<string, DailyUsageData & { apiKeys?: Set<string> }>();
  const projectMap = new Map<string, { id: string; name: string; status: 'active' | 'archived'; provider?: 'openai' | 'anthropic' }>();
  const uniqueApiKeyIds = new Set<string>();
  const partialErrors = sources.flatMap(source => source.partialErrors ?? []);

  let totalRequests = 0;
  let totalCost = 0;
  let totalContextTokens = 0;
  let totalGeneratedTokens = 0;
  let totalCachedTokens = 0;

  let minStart: number | undefined;
  let maxEnd: number | undefined;
  let timezoneOffsetMinutes: number | undefined;
  let coverageHours = 0;
  let expectedHours = 0;
  let singleSourceCoverageHours: number | undefined;
  let singleSourceExpectedHours: number | undefined;

  sources.forEach(source => {
    const provider = getProvider(source.apiKeyUsage?.[0]?.provider);

    totalRequests += source.totalRequests;
    totalCost += source.totalCost;
    totalContextTokens += source.totalContextTokens;
    totalGeneratedTokens += source.totalGeneratedTokens;
    totalCachedTokens += source.totalCachedTokens;

    source.modelUsage.forEach(model => {
      const modelProvider = getProvider(model.provider) ?? provider;
      const key = `${modelProvider ?? 'unknown'}:${model.model}`;
      const accumulator = modelMap.get(key) ?? {
        model: model.model,
        contextTokens: 0,
        generatedTokens: 0,
        cachedTokens: 0,
        cost: 0,
        numberOfRequests: 0,
        provider: modelProvider
      };
      accumulator.contextTokens += model.contextTokens;
      accumulator.generatedTokens += model.generatedTokens;
      accumulator.cachedTokens += model.cachedTokens;
      accumulator.cost += model.cost;
      accumulator.numberOfRequests += model.numberOfRequests;
      if (model.webSearchCalls !== undefined || accumulator.webSearchCalls !== undefined) {
        accumulator.webSearchCalls = model.webSearchCalls == null
          || accumulator.webSearchCalls === null
          || (modelMap.has(key) && accumulator.webSearchCalls === undefined)
          ? null
          : (accumulator.webSearchCalls ?? 0) + model.webSearchCalls;
      }
      if (model.lastUsed && (!accumulator.lastUsed || model.lastUsed > accumulator.lastUsed)) {
        accumulator.lastUsed = model.lastUsed;
      }
      modelMap.set(key, accumulator);
    });

    source.apiKeyUsage.forEach(entry => {
      const apiKeyProvider = getProvider(entry.provider) ?? provider;
      const key = `${apiKeyProvider ?? 'unknown'}:${entry.id}`;
      uniqueApiKeyIds.add(key);

      const ensureClone = (detail: ApiKeyUsageDetail): ApiKeyUsageDetail => {
        const cloned: ApiKeyUsageDetail = {
          ...detail,
          provider: apiKeyProvider,
          modelCosts: { ...(detail.modelCosts || {}) },
          dailyModelCosts: detail.dailyModelCosts
            ? Object.fromEntries(
                Object.entries(detail.dailyModelCosts).map(([date, models]) => [
                  date, { ...models }
                ])
              )
            : undefined,
          modelUsage: Object.fromEntries(
            Object.entries(detail.modelUsage || {}).map(([model, usage]) => [
              model,
              {
                ...usage,
                provider: apiKeyProvider ?? usage.provider
              }
            ])
          ),
          totalCost: detail.totalCost ?? detail.cost
        };
        return cloned;
      };

      if (!apiKeyMap.has(key)) {
        apiKeyMap.set(key, {
          id: key,
          provider: apiKeyProvider,
          entry: ensureClone(entry)
        });
        return;
      }

      const accumulator = apiKeyMap.get(key)!;
      const target = accumulator.entry;

      target.requests += entry.requests;
      target.contextTokens += entry.contextTokens;
      target.generatedTokens += entry.generatedTokens;
      target.cachedTokens += entry.cachedTokens;
      target.cost += entry.cost;
      target.cachedCost += entry.cachedCost;
      target.totalCost = (target.totalCost ?? 0) + (entry.totalCost ?? entry.cost);

      Object.entries(entry.modelCosts || {}).forEach(([model, value]) => {
        target.modelCosts[model] = (target.modelCosts[model] ?? 0) + value;
      });

      Object.entries(entry.modelUsage || {}).forEach(([model, usage]) => {
        if (!target.modelUsage[model]) {
          target.modelUsage[model] = {
            ...usage,
            provider: apiKeyProvider ?? usage.provider
          };
        } else {
          target.modelUsage[model].contextTokens += usage.contextTokens;
          target.modelUsage[model].generatedTokens += usage.generatedTokens;
          target.modelUsage[model].cachedTokens += usage.cachedTokens;
          target.modelUsage[model].cost += usage.cost;
          target.modelUsage[model].numberOfRequests += usage.numberOfRequests;
          if (usage.lastUsed && (!target.modelUsage[model].lastUsed || usage.lastUsed > target.modelUsage[model].lastUsed)) {
            target.modelUsage[model].lastUsed = usage.lastUsed;
          }
          if (!target.modelUsage[model].provider) {
            target.modelUsage[model].provider = apiKeyProvider ?? usage.provider;
          }
        }
      });

      // Merge dailyModelCosts
      if (entry.dailyModelCosts) {
        if (!target.dailyModelCosts) target.dailyModelCosts = {};
        Object.entries(entry.dailyModelCosts).forEach(([date, models]) => {
          if (!target.dailyModelCosts![date]) target.dailyModelCosts![date] = {};
          Object.entries(models).forEach(([model, cost]) => {
            target.dailyModelCosts![date][model] = (target.dailyModelCosts![date][model] ?? 0) + cost;
          });
        });
      }

      target.firstSeen = [target.firstSeen, entry.firstSeen].sort()[0];
      target.lastSeen = [target.lastSeen, entry.lastSeen].sort().reverse()[0];
    });

    source.dailyUsage?.forEach(day => {
      const apiKeys = new Set<string>(Array.isArray(day.apiKeys) ? day.apiKeys : Array.from(day.apiKeys ?? []));
      const dayProvider = day.provider ? getProvider(day.provider) ?? provider : provider;
      const breakdownClone = cloneBreakdowns(day.breakdowns);
      const existing = dailyMap.get(day.date);
      if (!existing) {
        const breakdowns = breakdownClone ?? (dayProvider
          ? {
              [dayProvider]: {
                provider: dayProvider,
                requests: day.requests,
                cost: day.cost,
                contextTokens: day.contextTokens,
                generatedTokens: day.generatedTokens,
                cachedTokens: day.cachedTokens,
                isEstimatedRequests: day.isEstimatedRequests ?? (dayProvider === 'anthropic')
              }
            }
          : undefined);
        dailyMap.set(day.date, {
          date: day.date,
          requests: day.requests,
          cost: day.cost,
          contextTokens: day.contextTokens,
          generatedTokens: day.generatedTokens,
          cachedTokens: day.cachedTokens,
          models: { ...day.models },
          modelCosts: day.modelCosts ? { ...day.modelCosts } : undefined,
          uniqueApiKeys: apiKeys.size,
          apiKeys,
          provider: dayProvider,
          breakdowns,
          isEstimatedRequests: day.isEstimatedRequests ?? (dayProvider === 'anthropic')
        });
      } else {
        existing.requests += day.requests;
        existing.cost += day.cost;
        existing.contextTokens += day.contextTokens;
        existing.generatedTokens += day.generatedTokens;
        existing.cachedTokens += day.cachedTokens;
        Object.entries(day.models).forEach(([model, value]) => {
          existing.models[model] = (existing.models[model] ?? 0) + value;
        });
        if (day.modelCosts) {
          if (!existing.modelCosts) existing.modelCosts = {};
          Object.entries(day.modelCosts).forEach(([model, value]) => {
            existing.modelCosts![model] = (existing.modelCosts![model] ?? 0) + value;
          });
        }
        Array.from(apiKeys).forEach(key => existing.apiKeys?.add(key));
        existing.uniqueApiKeys = existing.apiKeys?.size ?? existing.uniqueApiKeys;
        existing.isEstimatedRequests = existing.isEstimatedRequests || day.isEstimatedRequests || false;
        if (!existing.provider && dayProvider) {
          existing.provider = dayProvider;
        }
        if (breakdownClone) {
          Object.entries(breakdownClone).forEach(([key, value]) => {
            const providerKey = key as Provider;
            const breakdown = ensureBreakdown(existing, providerKey);
            breakdown.requests += value.requests;
            breakdown.cost += value.cost;
            breakdown.contextTokens += value.contextTokens;
            breakdown.generatedTokens += value.generatedTokens;
            breakdown.cachedTokens += value.cachedTokens;
            breakdown.isEstimatedRequests = breakdown.isEstimatedRequests || value.isEstimatedRequests;
          });
        } else if (dayProvider) {
          const fallback = ensureBreakdown(existing, dayProvider);
          fallback.requests += day.requests;
          fallback.cost += day.cost;
          fallback.contextTokens += day.contextTokens;
          fallback.generatedTokens += day.generatedTokens;
          fallback.cachedTokens += day.cachedTokens;
          fallback.isEstimatedRequests = fallback.isEstimatedRequests || day.isEstimatedRequests;
        }
      }
    });

    source.projects?.forEach(project => {
      if (!projectMap.has(project.id)) {
        projectMap.set(project.id, project);
      }
    });

    if (source.dataWindow) {
      const startMs = Date.parse(source.dataWindow.start);
      const endMs = Date.parse(source.dataWindow.end);
      if (!Number.isNaN(startMs)) {
        if (minStart === undefined || startMs < minStart) {
          minStart = startMs;
        }
      }
      if (!Number.isNaN(endMs)) {
        if (maxEnd === undefined || endMs > maxEnd) {
          maxEnd = endMs;
        }
      }
      if (timezoneOffsetMinutes === undefined) {
        timezoneOffsetMinutes = source.dataWindow.timezoneOffsetMinutes;
      }
      // Track individual source values for single-provider fallback
      singleSourceCoverageHours = source.dataWindow.coverageHours;
      singleSourceExpectedHours = source.dataWindow.expectedHours;
    }
  });

  // Compute coverageHours/expectedHours from the merged time window instead of
  // summing individual sources (which would double-count overlapping periods).
  // For a single source, preserve its original values to retain partial-coverage info.
  if (minStart !== undefined && maxEnd !== undefined) {
    if (sources.length === 1 && singleSourceCoverageHours !== undefined && singleSourceExpectedHours !== undefined) {
      coverageHours = singleSourceCoverageHours;
      expectedHours = singleSourceExpectedHours;
    } else {
      const windowHours = Math.max(1, Math.round((maxEnd - minStart) / (1000 * 60 * 60)));
      coverageHours = windowHours;
      expectedHours = windowHours;
    }
  }

  const combinedModelUsage = Array.from(modelMap.values()).sort((a, b) => b.cost - a.cost);
  const combinedApiKeyUsage = Array.from(apiKeyMap.values()).map(({ entry }) => entry);
  combinedApiKeyUsage.sort((a, b) => b.cost - a.cost);

  const combinedDailyUsage = Array.from(dailyMap.values()).map(day => ({
    ...day,
    apiKeys: day.apiKeys ?? new Set<string>(),
    breakdowns: cloneBreakdowns(day.breakdowns),
    isEstimatedRequests: day.isEstimatedRequests
  })).sort((a, b) => a.date.localeCompare(b.date));

  const dataWindow = minStart !== undefined && maxEnd !== undefined ? {
    start: new Date(minStart).toISOString(),
    end: new Date(maxEnd).toISOString(),
    timezoneOffsetMinutes: timezoneOffsetMinutes ?? new Date().getTimezoneOffset(),
    coverageHours,
    expectedHours
  } : undefined;

  return {
    modelUsage: combinedModelUsage,
    apiKeyUsage: combinedApiKeyUsage,
    totalRequests,
    totalCost,
    totalContextTokens,
    totalGeneratedTokens,
    totalCachedTokens,
    totalApiKeys: uniqueApiKeyIds.size,
    displayedApiKeys: uniqueApiKeyIds.size,
    dailyUsage: combinedDailyUsage,
    projects: Array.from(projectMap.values()),
    dataWindow,
    cacheTimestamp: Date.now(),
    partialErrors: partialErrors.length > 0 ? partialErrors : undefined
  };
};

export const mergeUsageForCharts = (
  sources: UsageChartMergeSource[],
  options?: MergeUsageForChartsOptions
): DailyUsageData[] => {
  if (!sources || sources.length === 0) {
    return [];
  }

  const joinMode = options?.join ?? 'outer';
  const providerSet = new Set<Provider>();
  const dateMap = new Map<string, DailyUsageData & { apiKeys?: Set<string> }>();

  sources.forEach(({ provider, dailyUsage }) => {
    providerSet.add(provider);
    dailyUsage.forEach(day => {
      const breakdownClone = cloneBreakdowns(day.breakdowns);
      const sourceBreakdown = breakdownClone?.[provider];
      const metrics: ProviderUsageMetrics = sourceBreakdown
        ? cloneProviderMetrics(sourceBreakdown)
        : {
            provider,
            requests: day.requests,
            cost: day.cost,
            contextTokens: day.contextTokens,
            generatedTokens: day.generatedTokens,
            cachedTokens: day.cachedTokens,
            isEstimatedRequests: day.isEstimatedRequests ?? (provider === 'anthropic')
          };

      const existing = dateMap.get(day.date);
      if (!existing) {
        const baseBreakdowns: Partial<Record<Provider, ProviderUsageMetrics>> = {
          [provider]: cloneProviderMetrics(metrics)
        };
        dateMap.set(day.date, {
          date: day.date,
          requests: metrics.requests,
          cost: metrics.cost,
          contextTokens: metrics.contextTokens,
          generatedTokens: metrics.generatedTokens,
          cachedTokens: metrics.cachedTokens,
          models: { ...day.models },
          modelCosts: day.modelCosts ? { ...day.modelCosts } : undefined,
          uniqueApiKeys: day.uniqueApiKeys,
          apiKeys: day.apiKeys instanceof Set
            ? new Set(day.apiKeys)
            : new Set(Array.isArray(day.apiKeys) ? day.apiKeys : []),
          provider: sources.length === 1 ? provider : undefined,
          breakdowns: baseBreakdowns,
          isEstimatedRequests: metrics.isEstimatedRequests,
          num_requests: day.num_requests ?? day.requests
        });
        return;
      }

      existing.requests += metrics.requests;
      existing.cost += metrics.cost;
      existing.contextTokens += metrics.contextTokens;
      existing.generatedTokens += metrics.generatedTokens;
      existing.cachedTokens += metrics.cachedTokens;
      existing.uniqueApiKeys = existing.apiKeys ? existing.apiKeys.size : Math.max(existing.uniqueApiKeys, day.uniqueApiKeys);
      if (!existing.apiKeys) {
        existing.apiKeys = new Set<string>();
      }
      const incomingKeys = day.apiKeys instanceof Set
        ? Array.from(day.apiKeys)
        : Array.isArray(day.apiKeys)
          ? day.apiKeys
          : [];
      incomingKeys.forEach(key => existing.apiKeys?.add(key));
      Object.entries(day.models).forEach(([model, value]) => {
        existing.models[model] = (existing.models[model] ?? 0) + value;
      });
      if (day.modelCosts) {
        if (!existing.modelCosts) existing.modelCosts = {};
        Object.entries(day.modelCosts).forEach(([model, value]) => {
          existing.modelCosts![model] = (existing.modelCosts![model] ?? 0) + value;
        });
      }

      const breakdown = ensureBreakdown(existing, provider);
      breakdown.requests += metrics.requests;
      breakdown.cost += metrics.cost;
      breakdown.contextTokens += metrics.contextTokens;
      breakdown.generatedTokens += metrics.generatedTokens;
      breakdown.cachedTokens += metrics.cachedTokens;
      breakdown.isEstimatedRequests = breakdown.isEstimatedRequests || metrics.isEstimatedRequests;

      existing.isEstimatedRequests =
        existing.isEstimatedRequests || metrics.isEstimatedRequests || false;
    });
  });

  let merged = Array.from(dateMap.values());

  if (joinMode === 'inner') {
    merged = merged.filter(entry => {
      const breakdowns = entry.breakdowns ?? {};
      return Array.from(providerSet).every(provider => breakdowns[provider]);
    });
  }

  const providers = Array.from(providerSet);

  return merged
    .map(entry => {
      const mergedBreakdowns = cloneBreakdowns(entry.breakdowns) ?? {};
      providers.forEach(provider => {
        if (!mergedBreakdowns[provider]) {
          mergedBreakdowns[provider] = {
            provider,
            requests: 0,
            cost: 0,
            contextTokens: 0,
            generatedTokens: 0,
            cachedTokens: 0,
            isEstimatedRequests: provider === 'anthropic'
          };
        }
      });
      const isEstimated = Object.values(mergedBreakdowns).some(metric => metric?.isEstimatedRequests);
      return {
        date: entry.date,
        requests: entry.requests,
        cost: entry.cost,
        contextTokens: entry.contextTokens,
        generatedTokens: entry.generatedTokens,
        cachedTokens: entry.cachedTokens,
        models: { ...entry.models },
        modelCosts: entry.modelCosts ? { ...entry.modelCosts } : undefined,
        uniqueApiKeys: entry.uniqueApiKeys,
        apiKeys: entry.apiKeys instanceof Set
          ? new Set(entry.apiKeys)
          : new Set(Array.isArray(entry.apiKeys) ? entry.apiKeys : []),
        provider: entry.provider,
        breakdowns: mergedBreakdowns,
        isEstimatedRequests: isEstimated,
        num_requests: entry.num_requests ?? entry.requests
      };
    })
    .sort((a, b) => a.date.localeCompare(b.date));
};
